import './ContractExplorerPage.scss';
import React from "react";
import {observer} from "mobx-react-lite";

import {Alert, IconButton, Skeleton, TextField, Tooltip} from "@mui/material";
import {CheckCircleRounded, DescriptionOutlined, FilterAltOutlined} from "@mui/icons-material";
import type {GridColDef, GridDataSource, GridFilterModel} from "@mui/x-data-grid-premium";
import {getGridSingleSelectOperators, useGridApiRef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "~/components/dashboard/DashboardCardConfigs.tsx";
import {searchInputProps} from "~/experiments/components/search-field/searchFieldSlotProps.ts";
import {useStores} from "~/stores";
import type {
    ClContractLifecycle,
    ClContractRow,
    ContractLifecycleCounts,
    ContractTypeCount,
} from "./contractExplorerTypes.ts";
import {
    daysUntilExpiry,
    formatAmount,
    formatDate,
    typeLabel,
    LIFECYCLE_COLOR,
    LIFECYCLE_LABEL,
    LIFECYCLES,
} from "./contractExplorerTypes.ts";
import {queryClient} from "~/query/queryClient.ts";
import {ContractDetailModal} from "./components/ContractDetailModal.tsx";
import {contractDetailUrl} from "./useContractDetailQuery.ts";
import {fetchContractDownloadUrl, fetchContractPage, useContractSummaryQuery} from "./useContractExplorerQueries.ts";
import {PAGE_SIZE_OPTIONS, toOrdering, toSortModel, useContractListParams} from "./useContractListParams.ts";
import {useContractNeighboursQuery} from "./useContractNeighboursQuery.ts";

const EXPIRING_DAYS = 90;
const SEARCH_DEBOUNCE_MS = 300;

// One card per lifecycle state, in the order a contract travels through them.
const LIFECYCLE_CARDS: {key: ClContractLifecycle; label: string; hint: string}[] = [
    {key: 'draft', label: 'Draft', hint: 'Not signed yet'},
    {key: 'signed', label: 'Signed', hint: 'Signed — effective period not started'},
    {key: 'active', label: 'Active', hint: 'In force, no renewal action needed yet'},
    {key: 'expiring', label: `Expiring ≤ ${EXPIRING_DAYS} days`, hint: 'Renewals to prepare — check terms'},
    {key: 'expired', label: 'Expired', hint: 'Past their expiration date'},
];

const EMPTY_LIFECYCLE_COUNTS: ContractLifecycleCounts =
    {draft: 0, signed: 0, active: 0, expiring: 0, expired: 0};
// Stable fallbacks while the summary is loading, so memo deps do not churn.
const NO_TYPE_COUNTS: ContractTypeCount[] = [];
const NO_KNOWN_TYPES: string[] = [];

// Filtering happens server-side (one value per field), so the pickers offer
// only equality — no "not" / "is any of".
const IS_ONLY = getGridSingleSelectOperators().filter(op => op.value === 'is');

export const ContractExplorerPage: React.FC = observer(() => {
    const {bagStore} = useStores();
    const bagId = bagStore.bagId;
    const apiRef = useGridApiRef();

    // Search / filter / sort live in the URL: coming back from a contract
    // restores the same list, and a filtered list is a shareable link.
    const {params: listParams, update: updateListParams} = useContractListParams();
    const {search, lifecycle: lifecycleFilter, type: typeFilter, contract: selectedId,
        page, pageSize} = listParams;
    const [searchInput, setSearchInput] = React.useState(search);

    // Whole-databag figures from `…/contracts/summary/`; they do not change
    // with search, filters or paging, so one query per databag serves them all.
    const {data: summary, isPending: summaryPending, error: summaryError} = useContractSummaryQuery(bagId);
    const lifecycleCounts = summary?.lifecycle_counts ?? null;
    // The contract types actually present in this databag, for the type filter.
    const typeCounts = summary?.type_counts ?? NO_TYPE_COUNTS;
    // The backend's canonical vocabulary, for the review form's suggestions.
    const knownTypes = summary?.known_types ?? NO_KNOWN_TYPES;
    // Reference date the backend calculated `lifecycle` against; the expiry
    // hint counts down from it so the two can never disagree.
    const asOf = summary?.as_of ?? '';
    // Base currency every amount is in (converted server-side at import).
    const currency = summary?.currency ?? '';
    // Rows matching the current search/filters (the list's `count`); drives the pager.
    const [total, setTotal] = React.useState<number | undefined>(undefined);
    const [error, setError] = React.useState<string | null>(null);
    // The grid would otherwise show its "No rows" overlay until the first
    // window arrives, which reads as "this databag has no contracts".
    const [loadingRows, setLoadingRows] = React.useState(true);
    // The contract with an open review draft, if any. Stored as the id so a
    // contract switch (deep link, back button) naturally leaves edit mode.
    const [editingId, setEditingId] = React.useState<number | null>(null);
    const editingContract = editingId !== null && editingId === selectedId;

    React.useEffect(() => {
        if (searchInput.trim() === search) return;
        const timer = setTimeout(() => updateListParams({search: searchInput.trim()}), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput, search, updateListParams]);

    const dataSource = React.useMemo<GridDataSource>(() => ({
        getRows: async ({start, end, sortModel}) => {
            const offset = typeof start === 'number' ? start : 0;
            setLoadingRows(true);
            try {
                const result = await fetchContractPage({
                    bagId,
                    offset,
                    limit: Math.max(end - offset + 1, 1),
                    search: search || undefined,
                    lifecycle: lifecycleFilter ?? undefined,
                    contractType: typeFilter ?? undefined,
                    ordering: sortModel.length > 0 ? toOrdering(sortModel) : listParams.ordering,
                });
                setTotal(result.total);
                setError(null);
                // rowCount drives the pager: "51-100 of 883" and the
                // last-page button.
                return {rows: result.rows, rowCount: result.total};
            } finally {
                setLoadingRows(false);
            }
        },
    }), [bagId, search, lifecycleFilter, typeFilter, listParams.ordering]);

    // Search and the status cards live outside the grid, so the grid has to be
    // told to start a fresh range walk when they change.
    const firstRender = React.useRef(true);
    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        apiRef.current?.dataSource?.fetchRows();
    }, [apiRef, search, lifecycleFilter, typeFilter]);

    // The link is minted per click so the permission check runs server-side and
    // no storage URLs are handed out for rows the user never opens.
    const openDocument = React.useCallback(async (contractId: number) => {
        try {
            const url = await fetchContractDownloadUrl(bagId, contractId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            setError('Could not open the document');
        }
    }, [bagId]);

    // The filter model lives in state rather than being derived from the URL:
    // opening the panel targeted at a column (showFilterPanel) appends an item
    // *without a value*, which URL params cannot represent — deriving the model
    // would drop that row and the panel would fall back to the first column.
    // The URL stays the source of truth for the *applied* filters; the state
    // only adds the row currently being edited.
    const [filterModel, setFilterModel] = React.useState<GridFilterModel>({items: []});

    // URL -> grid: cards, deep links and back/forward set the params; rebuild
    // the applied items from them, keeping any still-valueless in-edit row.
    React.useEffect(() => {
        setFilterModel(previous => {
            const applied = [
                ...lifecycleFilter
                    ? [{id: 'lifecycle', field: 'lifecycle', operator: 'is', value: lifecycleFilter}]
                    : [],
                ...typeFilter ? [{id: 'type', field: 'type', operator: 'is', value: typeFilter}] : [],
            ];
            const inEdit = previous.items.filter(item =>
                !item.value && !applied.some(a => a.field === item.field));
            return {items: [...applied, ...inEdit]};
        });
    }, [lifecycleFilter, typeFilter]);

    // grid -> URL: keep the panel's exact model (in-edit rows included) and
    // write only the valued items into the params.
    const onFilterModelChange = React.useCallback((model: GridFilterModel) => {
        setFilterModel(model);
        const valueOf = (field: string): string | null => {
            const item = model.items.find(i => i.field === field);
            return item?.value ? String(item.value) : null;
        };
        updateListParams({
            lifecycle: valueOf('lifecycle') as ClContractLifecycle | null,
            type: valueOf('type'),
        });
    }, [updateListParams]);

    /** Column title plus a permanently visible funnel that opens the filter
     *  panel targeted at this column — the menu-only entry point is too hidden. */
    const filteredHeader = React.useCallback((field: string, title: string, active: boolean) => (
        <span className="ce-filter-header">
            {title}
            <Tooltip title={`Filter by ${title.toLowerCase()}`}>
                <IconButton
                    size="small"
                    className={active ? 'is-filtering' : undefined}
                    onClick={event => {
                        // Just open the panel: don't let the click reach the
                        // header, where it would toggle sorting.
                        event.stopPropagation();
                        apiRef.current?.showFilterPanel(field);
                    }}>
                    <FilterAltOutlined fontSize="inherit"/>
                </IconButton>
            </Tooltip>
        </span>
    ), [apiRef]);

    /** Warm a contract's detail so opening the panel is instant. */
    const prefetchDetail = React.useCallback((contractId: number) => {
        void queryClient.prefetchQuery({queryKey: [contractDetailUrl(bagId, contractId)], staleTime: 30_000});
    }, [bagId]);

    // Opening/switching/closing the panel only rewrites a query param, so the
    // grid is never unmounted: no refetch, no lost scroll position.
    const selectContract = React.useCallback((contractId: number | null) => {
        updateListParams({contract: contractId});
    }, [updateListParams]);

    // The grid's row order lives in a ref, so changes to it do not re-render:
    // mirror it into state on the grid's own events. Without this, a contract
    // opened by link often has no neighbours, because the first window lands
    // after the last render.
    const [rowOrder, setRowOrder] = React.useState<number[]>([]);
    React.useEffect(() => {
        const api = apiRef.current;
        if (!api?.subscribeEvent) return;
        const sync = () => setRowOrder(previous => {
            const ids = (api.getSortedRowIds?.() ?? []).filter((id): id is number => typeof id === 'number');
            // Same contents must keep the same array identity: these events also
            // fire while re-rendering, so a fresh array here would re-render the
            // page, fire them again, and loop.
            const unchanged = ids.length === previous.length && ids.every((id, i) => id === previous[i]);
            return unchanged ? previous : ids;
        });
        // Deferred, because a synchronous update here would cascade renders —
        // and it covers rows the grid had already set before we subscribed.
        const initial = setTimeout(sync, 0);
        const unsubscribe = [api.subscribeEvent('rowsSet', sync), api.subscribeEvent('sortedRowsSet', sync)];
        return () => {
            clearTimeout(initial);
            unsubscribe.forEach(off => off());
        };
    }, [apiRef]);

    // Suggestions for the review form: what this databag actually uses first
    // (most common first), then the rest of the canonical vocabulary.
    const typeSuggestions = React.useMemo(() => {
        const used = typeCounts.filter(t => t.is_known && t.contract_type).map(t => t.contract_type);
        const rest = knownTypes.filter(t => !used.includes(t)).sort();
        return [...used, ...rest].map(typeLabel);
    }, [typeCounts, knownTypes]);

    const loadedIndex = selectedId ? rowOrder.indexOf(selectedId) : -1;
    // Whatever the grid has loaded answers for free; a contract outside that
    // window (a link into the tail of a long list, or one the filter excludes)
    // needs the server, which knows the whole ordered list.
    const {data: serverNeighbours} = useContractNeighboursQuery(
        bagId, selectedId ?? 0, listParams, selectedId !== null && loadedIndex === -1,
    );

    const neighbour = (delta: number): number | null => {
        if (!selectedId) return null;
        if (loadedIndex !== -1) return rowOrder[loadedIndex + delta] ?? null;
        return (delta < 0 ? serverNeighbours?.previous_id : serverNeighbours?.next_id) ?? null;
    };

    const previousId = neighbour(-1);
    const nextId = neighbour(1);
    // ↑/↓ walk the list while the panel is open.
    React.useEffect(() => {
        if (!selectedId || editingContract) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            const target = event.target as HTMLElement | null;
            if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
            const target_id = event.key === 'ArrowDown' ? nextId : previousId;
            if (target_id === null) return;
            event.preventDefault();
            selectContract(target_id);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedId, editingContract, nextId, previousId, selectContract]);

    const columns = React.useMemo<GridColDef<ClContractRow>[]>(() => [
        {
            field: 'title',
            headerName: 'Contract',
            filterable: false,
            flex: 1.8,
            minWidth: 240,
            renderCell: ({row}) => (
                <span className="ce-title">
                    {row.title}
                    {row.hasReview && <span className="ce-edited" title={`Edited (v${row.versionNo})`}>edited</span>}
                </span>
            ),
        },
        {field: 'supplier', headerName: 'Supplier', flex: 1.2, minWidth: 180, filterable: false},
        {
            field: 'type',
            headerName: 'Type',
            flex: 1,
            minWidth: 150,
            type: 'singleSelect',
            // The databag's actual vocabulary, from the list response. The filter
            // value is the cleaned slug (what the API takes); the cell shows the
            // label, which is fine because no filtering happens client-side.
            valueOptions: typeCounts
                .filter(t => t.contract_type)
                .map(t => ({value: t.contract_type, label: `${typeLabel(t.contract_type)} (${t.count})`})),
            filterOperators: IS_ONLY,
            renderHeader: () => filteredHeader('type', 'Type', typeFilter !== null),
            // The cell holds the display label, not the option slug, so bypass
            // singleSelect's value->option lookup (it would render nothing).
            renderCell: ({row}) => row.type,
        },
        {
            field: 'lifecycle',
            headerName: 'Lifecycle',
            flex: 0.7,
            minWidth: 120,
            sortable: false,
            type: 'singleSelect',
            valueOptions: LIFECYCLES.map(value => ({value, label: LIFECYCLE_LABEL[value]})),
            filterOperators: IS_ONLY,
            renderHeader: () => filteredHeader('lifecycle', 'Lifecycle', lifecycleFilter !== null),
            renderCell: ({row}) =>
                <Tag label={LIFECYCLE_LABEL[row.lifecycle]} color={LIFECYCLE_COLOR[row.lifecycle]}/>,
        },
        {
            field: 'effectiveDate',
            headerName: 'Effective',
            filterable: false,
            flex: 0.8,
            minWidth: 130,
            valueFormatter: (value: string | null) => formatDate(value),
        },
        {
            field: 'expirationDate',
            headerName: 'Expires',
            filterable: false,
            flex: 0.9,
            minWidth: 150,
            renderCell: ({row}) => {
                const days = daysUntilExpiry(row.expirationDate, asOf);
                return (
                    <span>
                        {formatDate(row.expirationDate)}
                        {row.lifecycle === 'expiring' && days !== null &&
                            <span className="ce-expiry-hint"> · in {days}d</span>}
                    </span>
                );
            },
        },
        {
            field: 'contractedAmount',
            filterable: false,
            // Base currency in the header rather than per cell — every amount in
            // the list is in it, so repeating the code 50 times is just noise.
            headerName: currency ? `Contracted amount (${currency})` : 'Contracted amount',
            type: 'number',
            flex: 0.9,
            minWidth: 160,
            valueFormatter: (value: number) => formatAmount(value),
        },
        {
            field: 'hasFile',
            headerName: 'Document',
            filterable: false,
            flex: 0.6,
            minWidth: 110,
            sortable: false,
            renderCell: ({row}) => (row.hasFile
                ? (
                    <Tooltip title={row.fileName || 'Open the source document'}>
                        <IconButton
                            size="small"
                            aria-label={`Open ${row.fileName || 'document'}`}
                            onClick={event => {
                                event.stopPropagation();
                                void openDocument(row.id);
                            }}>
                            <DescriptionOutlined fontSize="small" className="ce-hasfile"/>
                        </IconButton>
                    </Tooltip>
                )
                : <span className="ce-nofile" title="No source document uploaded">—</span>),
        },
    ], [asOf, currency, typeCounts, openDocument, filteredHeader, lifecycleFilter, typeFilter]);

    const counts = lifecycleCounts ?? EMPTY_LIFECYCLE_COUNTS;
    const countsPending = summaryPending && summaryError === null;
    // The list windows and the summary are separate requests; either failing
    // means the page is not showing the contracts as they are.
    const loadError = error ?? summaryError?.message ?? null;

    return (
        <MainPageFacade title={CARD_CONFIG['contract-explorer'].title} className="contract-explorer-page">
            {loadError && <Alert severity="error" className="ce-alert">Could not load the contracts: {loadError}</Alert>}

            <div className="ce-status-cards">
                {LIFECYCLE_CARDS.map(({key, label, hint}) => {
                    const isActive = lifecycleFilter === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`ce-status-card tone-${key}${isActive ? ' is-active' : ''}`}
                            aria-pressed={isActive}
                            onClick={() => updateListParams({lifecycle: lifecycleFilter === key ? null : key})}>
                            <span className="ce-card-head">
                                <span className="ce-card-dot"/>
                                {label}
                                {isActive
                                    ? <CheckCircleRounded className="ce-card-check" fontSize="inherit"/>
                                    : <span className="ce-card-filterhint">Filter</span>}
                            </span>
                            <span className="ce-card-value">
                                {countsPending ? <Skeleton width={40}/> : counts[key]}
                            </span>
                            <span className="ce-card-hint">{hint}</span>
                        </button>
                    );
                })}
            </div>

            <div className="ce-toolbar">
                <TextField
                    className="ce-search"
                    type="search"
                    size="small"
                    placeholder="Search contract, supplier or file…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    slotProps={{htmlInput: searchInputProps('contract-search', 'Search contracts')}}
                />
                <span className="ce-rowcount">
                    {total === undefined ? '' : `${total} contracts`}
                </span>
            </div>

            {/* Bounded height: the grid scrolls its own viewport, so the pager
                stays put at the bottom instead of being pushed off-screen. */}
            <div className="ce-table-wrap">
                <CustomDataGrid
                className="ce-table"
                apiRef={apiRef}
                columns={columns}
                dataSource={dataSource}
                // The grid's own cache key ignores our search/status params, so
                // caching is left to the query client instead.
                dataSourceCache={null}
                virtualized
                loading={loadingRows}
                rowCount={total ?? 0}
                pagination
                paginationMode="server"
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                // The page lives in the URL like every other list parameter, so
                // a link points at what the sender was looking at and coming
                // back from a contract lands on the same page.
                paginationModel={{page, pageSize}}
                onPaginationModelChange={model =>
                    updateListParams({page: model.page, pageSize: model.pageSize})}
                sortingMode="server"
                onSortModelChange={model => updateListParams({ordering: toOrdering(model)})}
                filterModel={filterModel}
                onFilterModelChange={onFilterModelChange}
                // Read once, at mount: the sort indicator matches the URL's `ordering`.
                initialState={{sorting: {sortModel: toSortModel(listParams.ordering)}}}
                onDataSourceError={e => setError(e.message)}
                onRowClick={({row}) => selectContract(row.id)}
                slotProps={{row: {onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
                    const id = Number(e.currentTarget.getAttribute('data-id'));
                    if (id) prefetchDetail(id);
                }}}}
                rowSelectionModel={{type: 'include', ids: new Set(selectedId ? [selectedId] : [])}}
                disableRowSelectionOnClick
                />
            </div>

            {/* Row click opens the detail here, over the list: the grid is never
                unmounted, so closing keeps scroll position, loaded chunks and filters. */}
            {selectedId && (
                <ContractDetailModal
                    bagId={bagId}
                    contractId={selectedId}
                    onClose={() => selectContract(null)}
                    onOpenDocument={openDocument}
                    editing={editingContract}
                    onEditingChange={next => setEditingId(next ? selectedId : null)}
                    onOpenContract={selectContract}
                    typeSuggestions={typeSuggestions}
                    // The saved contract's row is stale in the grid (title,
                    // amount), and the mutation invalidated the windows.
                    onSaved={() => apiRef.current?.dataSource?.fetchRows()}
                    onPrevious={previousId !== null ? () => selectContract(previousId) : undefined}
                    onNext={nextId !== null ? () => selectContract(nextId) : undefined}
                />
            )}
        </MainPageFacade>
    );
});
